import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { ActivityCard } from "./activity-card";
import { Module, Section, Activity, Question } from "../../schemas/schemas";

interface ModuleCardProps {
  module: Module;
  moduleIndex: number;
  onActivityContentChange: (activityId: string, newContent: string) => void;
  onOpenQuestionBank: (activityId: string) => void;
  onEditQuestion: (question: Question) => void;
  onRemoveQuestion: (questionId: string) => void;
  t: (key: string) => string;
}

export function ModuleCard({
  module,
  moduleIndex,
  onActivityContentChange,
  onOpenQuestionBank,
  onEditQuestion,
  onRemoveQuestion,
  t,
}: ModuleCardProps) {
  // Helper function to get delivery method styling
  const getDeliveryMethodStyle = (method: string) => {
    switch (method) {
      case 'combined': 
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'e-learning': 
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'live-online': 
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-person': 
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offline': 
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default: 
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Helper function to get delivery method icon
  const getDeliveryMethodIcon = (method: string) => {
    switch (method) {
      case 'combined':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      case 'e-learning':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'live-online':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'in-person':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'offline':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Helper function to translate delivery methods to Vietnamese
  const getDeliveryMethodLabel = (method: string) => {
    switch (method) {
      case 'combined': return 'Học tập tổng hợp';
      case 'e-learning': return 'Học trực tuyến';
      case 'live-online': return 'Học trực tiếp online';
      case 'in-person': return 'Học trực tiếp';
      case 'offline': return 'Học tại lớp';
      default: return method;
    }
  };

  return (
    <div key={module.id} className="space-y-6">
      <Card>
        <CardHeader className="bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {module.name}
              </CardTitle>
              <CardDescription className="flex items-center">
                <span className="mr-2">Loại:</span>
                <div className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border ${getDeliveryMethodStyle(module.type)}`}>
                  {getDeliveryMethodIcon(module.type)}
                  {getDeliveryMethodLabel(module.type)}
                </div>
                <span className="ml-2 text-gray-500">| {(module.sections || []).length} phần</span>
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                {t('course-content.composer.addContent')}
              </Button>
              <Button variant="outline" size="sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                {t('course-content.composer.addExercise')}
              </Button>
              <Button variant="outline" size="sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Add Session
              </Button>
              <Button variant="ghost" size="sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                </svg>
                {t('course-content.composer.editModule')}
              </Button>
              <Button variant="ghost" size="sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
                {t('course-content.composer.delete')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 ps-0 pe-0 pl-0 pr-0">
          <div className="p-0">
            {/* Activities */}
            <div className="space-y-0">
              {(module.sections || []).map((section: Section) => (
                <div key={section.id}>
                  {/* Section Header */}
                  <Card className="border-0 border-t rounded-none border-gray-200 dark:border-gray-700 mb-0">
                    <CardHeader className="py-2 px-4 bg-blue-50 dark:bg-blue-900/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="mr-2 text-blue-600">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15V10a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"></path>
                              <path d="M17 10h4"></path>
                              <path d="M19 13h2"></path>
                            </svg>
                          </span>
                          <div>
                            <div className="font-medium text-sm">
                              Phần {section.order}: {section.name}
                            </div>
                            <div className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border mt-1 ${getDeliveryMethodStyle(section.delivery_method)}`}>
                              {getDeliveryMethodIcon(section.delivery_method)}
                              {getDeliveryMethodLabel(section.delivery_method)} | {(section.activities || []).length} hoạt động
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 20h9"></path>
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                            </svg>
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Activities in this section */}
                  <div className="space-y-0">
                    {(section.activities || []).map((activity: Activity) => (
                      <ActivityCard
                        key={activity.activity_id}
                        activity={activity}
                        onActivityContentChange={onActivityContentChange}
                        onOpenQuestionBank={onOpenQuestionBank}
                        onEditQuestion={onEditQuestion}
                        onRemoveQuestion={onRemoveQuestion}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 